"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { type User, signInWithEmailAndPassword, signInWithPopup, createUserWithEmailAndPassword, sendPasswordResetEmail, onAuthStateChanged, onIdTokenChanged, signInAnonymously, updateProfile } from "firebase/auth";
import { firebaseManager } from "@/lib/firebase";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { setAuthToken, removeAuthToken, clearAllAuthData } from "./auth-utils";
import { linkWithEmail, linkWithGoogle, signInAfterCollision } from "./account-linking";
import type { AuthClientConfig } from "./authClientConfig";
import { AuthContext } from "./AuthContext";
import type { AuthContextType, AuthDialogState, DialogRequest, DialogChoice } from "./AuthContext";
import { hasActivity } from "./activityTracker";
export class SignUpAbortedError extends Error {
  constructor(public readonly reason: "use-different-email" | "keep-guest") {
    super(`sign-up aborted: ${reason}`);
    this.name = "SignUpAbortedError";
  }
}
export class SignInAbortedError extends Error {
  constructor() {
    super("sign-in aborted: user chose to keep playing as guest");
    this.name = "SignInAbortedError";
  }
}
function toAuthUser(firebaseUser: User): NonNullable<AuthContextType["user"]> {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    isAnonymous: firebaseUser.isAnonymous
  };
}
interface AuthProviderProps {
  children: React.ReactNode;
  /** Server-derived auth slice of the tenant config (see `toAuthClientConfig`). */
  auth: AuthClientConfig;
}

/**
 * Multi-tenant aware AuthProvider with Firebase.
 *
 * This component imports firebase/auth and should only be loaded when
 * authentication is enabled for the tenant. For disabled tenants,
 * use AuthProviderDisabled instead (no Firebase bundle cost).
 */
export function AuthProvider(t0) {
  const $ = _c(27);
  const {
    children,
    auth: authConfig
  } = t0;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sdk = useFansUnitedSDK();
  const {
    firebaseConfig,
    anonymous: anonymousEnabled
  } = authConfig;
  const isAuthEnabled = authConfig.enabled && firebaseConfig !== null;
  let t1;
  let t2;
  if ($[0] !== anonymousEnabled || $[1] !== firebaseConfig || $[2] !== sdk) {
    t1 = () => {
      if (!firebaseConfig) {
        setLoading(false);
        return;
      }
      const {
        auth
      } = firebaseManager.initialize(firebaseConfig);
      const unsubscribeToken = onIdTokenChanged(auth, _temp);
      const unsubscribe = onAuthStateChanged(auth, async firebaseUser_0 => {
        if (firebaseUser_0) {
          const token_0 = await firebaseUser_0.getIdToken();
          setAuthToken(token_0);
          setUser(toAuthUser(firebaseUser_0));
          if (!firebaseUser_0.isAnonymous && sdk) {
            sdk.profile.getOwn().getInfo().catch(_temp2);
          }
        } else {
          if (anonymousEnabled) {
            ;
            try {
              await signInAnonymously(auth);
              return;
            } catch (t3) {
              const err = t3;
              console.error("AuthProvider: signInAnonymously failed", err);
            }
          }
          removeAuthToken();
          setUser(null);
        }
        setLoading(false);
      });
      return () => {
        unsubscribe();
        unsubscribeToken();
      };
    };
    t2 = [firebaseConfig, sdk, anonymousEnabled];
    $[0] = anonymousEnabled;
    $[1] = firebaseConfig;
    $[2] = sdk;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const [dialogRequest, setDialogRequest] = useState(null);
  const dialogResolverRef = useRef(null);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = request => new Promise(resolve => {
      dialogResolverRef.current = resolve;
      setDialogRequest(request);
    });
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const openDialog = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = choice => {
      const resolver = dialogResolverRef.current;
      dialogResolverRef.current = null;
      setDialogRequest(null);
      if (resolver) {
        resolver(choice);
      }
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const chooseDialog = t4;
  let t5;
  if ($[7] !== sdk) {
    t5 = async firebaseUser_1 => {
      const token_1 = await firebaseUser_1.getIdToken();
      setAuthToken(token_1);
      setUser(toAuthUser(firebaseUser_1));
      if (!sdk) {
        return;
      }
      let profileUpdate = sdk.profile.getOwn();
      if (firebaseUser_1.email) {
        profileUpdate = profileUpdate.setEmail(firebaseUser_1.email);
      }
      if (firebaseUser_1.displayName) {
        profileUpdate = profileUpdate.setName(firebaseUser_1.displayName);
      }
      if (firebaseUser_1.photoURL) {
        profileUpdate = profileUpdate.setAvatar(firebaseUser_1.photoURL);
      }
      ;
      try {
        await profileUpdate.update();
      } catch (t6) {
        const err_0 = t6;
        console.warn("Failed to sync FansUnited profile after account upgrade:", err_0);
      }
    };
    $[7] = sdk;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const applyUpgradedUser = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = async (email, password) => {
      const auth_0 = firebaseManager.getAuth();
      if (!auth_0) {
        throw new Error("Authentication not configured");
      }
      const current = auth_0.currentUser;
      if (current?.isAnonymous && hasActivity(current.uid)) {
        const choice_0 = await openDialog({
          kind: "switch-from-guest"
        });
        if (choice_0 === "keep-guest") {
          throw new SignInAbortedError();
        }
        if (choice_0 === "use-different-email") {
          throw new SignInAbortedError();
        }
      }
      await signInWithEmailAndPassword(auth_0, email, password);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const signIn = t6;
  let t7;
  if ($[10] !== applyUpgradedUser) {
    t7 = async (email_0, password_0, displayName) => {
      const auth_1 = firebaseManager.getAuth();
      if (!auth_1) {
        throw new Error("Authentication not configured");
      }
      const current_0 = auth_1.currentUser;
      if (current_0?.isAnonymous) {
        const linkResult = await linkWithEmail(current_0, email_0, password_0);
        if (!linkResult.ok) {
          const choice_1 = await openDialog({
            kind: "collision-email",
            email: email_0,
            password: password_0
          });
          if (choice_1 === "sign-in-existing") {
            await signInAfterCollision(auth_1, email_0, password_0, null);
            return;
          }
          throw new SignUpAbortedError(choice_1);
        }
        if (displayName) {
          await updateProfile(current_0, {
            displayName
          });
        }
        await applyUpgradedUser(current_0);
        return;
      }
      const result = await createUserWithEmailAndPassword(auth_1, email_0, password_0);
      if (displayName) {
        await updateProfile(result.user, {
          displayName
        });
      }
      const token_2 = await result.user.getIdToken();
      setAuthToken(token_2);
    };
    $[10] = applyUpgradedUser;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const signUp = t7;
  let t8;
  if ($[12] !== applyUpgradedUser) {
    t8 = async () => {
      const auth_2 = firebaseManager.getAuth();
      const provider = firebaseManager.getGoogleProvider();
      if (!auth_2 || !provider) {
        throw new Error("Authentication not configured");
      }
      const current_1 = auth_2.currentUser;
      if (current_1?.isAnonymous) {
        const linkResult_0 = await linkWithGoogle(current_1, provider);
        if (linkResult_0.ok) {
          await applyUpgradedUser(current_1);
          return;
        }
        const choice_2 = await openDialog({
          kind: "collision-google",
          email: linkResult_0.collision.email,
          credential: linkResult_0.collision.credential
        });
        if (choice_2 === "sign-in-existing") {
          await signInAfterCollision(auth_2, null, null, linkResult_0.collision.credential);
          return;
        }
        throw new SignUpAbortedError(choice_2 === "keep-guest" ? "keep-guest" : "use-different-email");
      }
      await signInWithPopup(auth_2, provider);
    };
    $[12] = applyUpgradedUser;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const signInWithGoogle = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async () => {
      await clearAllAuthData();
      setUser(null);
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const signOut = t9;
  const resetPassword = _temp3;
  let t10;
  if ($[15] !== dialogRequest) {
    t10 = {
      request: dialogRequest,
      choose: chooseDialog
    };
    $[15] = dialogRequest;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const dialog = t10;
  let t11;
  if ($[17] !== dialog || $[18] !== isAuthEnabled || $[19] !== loading || $[20] !== signInWithGoogle || $[21] !== signUp || $[22] !== user) {
    t11 = {
      user,
      loading,
      isAuthEnabled,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
      dialog
    };
    $[17] = dialog;
    $[18] = isAuthEnabled;
    $[19] = loading;
    $[20] = signInWithGoogle;
    $[21] = signUp;
    $[22] = user;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const value = t11;
  let t12;
  if ($[24] !== children || $[25] !== value) {
    t12 = <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
    $[24] = children;
    $[25] = value;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
async function _temp3(email_1) {
  const auth_3 = firebaseManager.getAuth();
  if (!auth_3) {
    throw new Error("Authentication not configured");
  }
  await sendPasswordResetEmail(auth_3, email_1);
}
function _temp2() {}
async function _temp(firebaseUser) {
  if (!firebaseUser) {
    return;
  }
  const token = await firebaseUser.getIdToken();
  setAuthToken(token);
}